import {
    AbsenceLimitGlobalResponseDto,
    AbsenceLimitRestrictionGlobalResponseDto,
    AbsenceTypeLimitResponseDto,
    AbsenceTypeResponseDto,
} from '@/domain/myOrganization';
import { PREDEFINED_ABSENCE_TYPE } from '@/pages/AdministratorPanel/ManageRequests/AbsenceTypes/constants';
import { PERIOD } from '@/utils/enums/common';
import { GET_LIMITS_USAGE_RANGE_TYPE } from '@/utils/enums/newRequest';
import { DELETION_STATUS, REQUEST_STATUS } from '@/utils/enums/requestsPage';

export interface RequestResponseDto {
    _id: string;
    createdAt: string;
    updatedAt: string;
    userId: string;
    organizationId: string;
    from: string;
    to: string;
    type: PREDEFINED_ABSENCE_TYPE;
    absenceTypeId: string;
    status: REQUEST_STATUS | DELETION_STATUS;
    statusInRequest?: boolean | null;
    comment: string;
    removed?: boolean | null;
    isOneDay: boolean;
    isWorking: boolean;
    totalWorkDays: number;
}

export interface RequestWithDates extends Omit<RequestResponseDto, 'from' | 'to'> {
    from: Date;
    to: Date;
    color?: string;
    isCustomType?: boolean;
    isHiddenType?: boolean;
}

export interface CreateRequestParamsDto {
    from: string;
    to: string;
    type?: string;
    status?: REQUEST_STATUS;
    comment: string;
    absenceTypeId: string;
}
export interface RequestPreviewParamsDto {
    userId: string;
    requestBody: CreateRequestParamsDto;
}

export interface OwnLimitsUsageParamsDto {
    rangeType: GET_LIMITS_USAGE_RANGE_TYPE;
    endDate?: string;
    startDate?: string;
    includeArchived?: boolean;
}

export interface OwnLimitsUsageParamsDtoApiV2 {
    includeArchived?: boolean;
    date: string;
}

export interface OwnLimitsUsageParamsDtoApiV3 {
    includeArchived?: boolean;
    includeArchivedLimits?: boolean;
    date: string;
}

export interface UserLimitsUsageParamsDtoApiV4 extends OwnLimitsUsageParamsDtoApiV3 {
    userId: string;
}

export interface DashboardLimitsUsageParamsDto {
    date: string;
    includeArchived?: boolean;
}

export type RequestLimitRow = {
    absenceTypeId: string;
    name: string;
    icon?: string;
    color?: string;
    daysUsed: number;
    limitValue?: number;
    daysAvailableToUse?: number;
    noLimits: boolean;
    carryoverDays?: number;
    periodType?: PERIOD;
    isChild: boolean;
};

export interface LimitsUsageParamsDto extends OwnLimitsUsageParamsDto {
    userId: string;
}

export type LimitsUsageResponseDto = [
    {
        user: {
            credentialId: string;
            username: string;
            firstName: string;
            lastName: string;
            email: string;
            activated: boolean;
            banned: boolean;
            language: string;
            languageUpdatedAt: string;
            profilePictureUrl: string;
        };
        userId: string;
        organizationId: string;
        buckets: Limit[];
    }
];

export type OwnLimitsUsageResponseV3 = Array<
    Limit & {
        archivedGlobalLimits: string[];
        archivedPersonalLimits: string[];
        isArchived: boolean;
        globalLimit: AbsenceLimitGlobalResponseDto;
        additionalLimit?: { additionalLimitDays: number };
        daysAvailableToUse?: number;
    }
>;

export interface Limit {
    type: string;
    absenceTypeId: string;
    daysUsed: number;
    absenceType: AbsenceTypeResponseDto;
    limit: AbsenceTypeLimitResponseDto;
    restriction?: LimitRestriction;
}

type LimitRestriction = AbsenceLimitRestrictionGlobalResponseDto & { isPersonalRestriction: boolean };

export interface UserApproverResponseDto {
    createdAt: string;
    credentialId: string;
    managerId: string;
    organizationId: string;
    updatedAt: string;
    _id: string;
}

export interface RequestPreviewResponseDto {
    absenceLimitType: AbsenceTypeLimitResponseDto;
    absenceType: string;
    absenceTypeDetail: AbsenceTypeResponseDto;
    daysAvailable: number;
    isOverLimit: boolean;
    ranges: RequestPreviewRanges[];
    restriction?: AbsenceLimitRestrictionGlobalResponseDto;
}

export interface RequestPreviewRanges {
    startDate: string;
    endDate: string;
    daysUsed: number;
    daysUsedTotal: number;
    daysUsedAdditional: number;
    daysLeft: number;
    isOverLimit: boolean;
    daysUsedByOtherTypesWithinLimit: number;
}

export interface RangesInfo {
    isCurrentAndFuture: boolean;
    currentPeriodStartDate: string;
    currentPeriodEndDate: string;
    lastPeriodStartDate: string;
    lastPeriodEndDate: string;
}
export interface RequestSummary {
    noLimits?: boolean;
    limit?: number;
    periodType?: PERIOD;
    daysUsed?: number;
    daysUsedAdditional?: number;
    allowOverbooking?: boolean;
    leftDays?: number;
    isOverLimit?: boolean;
    parentWithRestriction?: PERIOD;
    limitParent?: number;
    limitChild?: number;
    daysUsedChild?: number;
    daysUsedParent?: number;
    parentName?: string;
    childName?: string;
    childLeftDays?: number;
    daysAvailable: number;
    includeNextRange?: RangesInfo;
}

export interface RequestNoRestrictionSummary {
    noLimits?: boolean;
    periodType?: PERIOD;
    daysUsed?: number;
    daysUsedAdditional?: number;
    leftDays?: number;
    isOverLimit?: boolean;
    limit?: number;
    allowOverbooking?: boolean;
    daysAvailable: number;
    includeNextRange?: RangesInfo;
}

export interface RequestWithRestrictionSummary {
    noLimits?: boolean;
    periodType?: PERIOD;
    daysUsedAdditional?: number;
    parentWithRestriction?: PERIOD;
    limitParent?: number;
    daysUsedChild?: number;
    daysUsedParent?: number;
    parentName?: string;
    childName?: string;
    childLeftDays?: number;
    isOverLimit?: boolean;
    limit?: number;
    allowOverbooking?: boolean;
    daysAvailable: number;
    includeNextRange?: RangesInfo;
}

export enum NewRequestWhere {
    webSchedule = 'WebSchedule',
}
